import React, { createContext, useContext, useEffect, useState } from 'react';
import { authService } from '../utils/authService';
import { authApi } from '../utils/authApi';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(_userId) {
      setProfileLoading(true)
      try {
        const token = localStorage?.getItem('auth_token')
        const { data, error } = await authApi?.getMyProfile({ token })
        if (!error && data) setUserProfile(data)
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Local auth state bootstrap and handlers
  const authStateHandlers = {
    bootstrap: async () => {
      try {
        const token = localStorage?.getItem('auth_token')
        const storedUser = localStorage?.getItem('auth_user')
        if (token && storedUser) {
          const parsedUser = JSON.parse(storedUser)
          setUser(parsedUser)
          profileOperations?.load()
          return
        }

        if (token && !storedUser) {
          const { data, error } = await authService?.getCurrentUser(token)
          if (!error && data) {
            const userObj = data?.user || data
            if (userObj) {
              localStorage?.setItem('auth_user', JSON.stringify(userObj))
              setUser(userObj)
              profileOperations?.load()
              return
            }
          }
        }

        profileOperations?.clear()
      } catch (e) {
        console.error('Auth bootstrap error:', e)
        profileOperations?.clear()
      } finally {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    // Bootstrap from localStorage (REST-based auth)
    authStateHandlers?.bootstrap()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await authService?.signIn(email, password)      
      if (!error && data) {
        const { access_token, Token, token, user: userObj } = data || {}
        const resolvedToken = access_token || Token || token
        if (resolvedToken) localStorage?.setItem('auth_token', resolvedToken)
        if (userObj) {
          localStorage?.setItem('auth_user', JSON.stringify(userObj))
          setUser(userObj)
          profileOperations?.load()
        }
      }
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      // Clear REST-based session
      localStorage?.removeItem('auth_token')
      localStorage?.removeItem('auth_user')
      setUser(null)
      profileOperations?.clear()
      return { error: null }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const refreshProfile = async () => {
    try {
      await profileOperations?.load()
    } catch (_) {}
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }

    try {
      const token = localStorage?.getItem('auth_token')
      const first = updates?.first_name
      const last = updates?.last_name
      const mappedPayload = {
        first_name: first,
        last_name: last,
        full_name: (typeof first === 'string' || typeof last === 'string')
          ? `${first || ''} ${last || ''}`.trim()
          : undefined,
        bio: updates?.bio,
        phone_number: updates && Object.prototype.hasOwnProperty.call(updates, 'phone')
          ? (updates?.phone ?? '')
          : undefined,
        metadata: {
          website_url: updates?.website_url ?? '',
          location: updates?.location ?? '',
          default_language: updates?.default_language ?? ''
        }
      }

      const { data, error } = await authApi?.updateMe({ token, payload: mappedPayload })
      if (!error) {
        await profileOperations?.load()
      }
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    refreshProfile,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
